import { getClassName } from 'common/utils';
import { clamp } from 'lodash';
import React, { useMemo } from 'react';

interface ProgressBarValueProps {
  value: number;
  type: 'primary' | 'secondary' | 'background';
}
// eslint-disable-next-line react/display-name
const ProgressBarValue = React.memo((props: ProgressBarValueProps) => {
  return (
    <div
      data-test-id={`progressbar-${props.type}`}
      data-value={props.value}
      className={`value ${props.type}`}
      style={{ width: `${props.value}%` }}
    />
  );
});

interface ProgressBarProps {
  value: number | number[];
  active?: boolean;
  customRef?: React.RefObject<HTMLDivElement>;
  safeArea?: boolean;
  background?: boolean;
}

const Progressbar = ({
  value,
  active,
  customRef,
  safeArea = true,
  children,
  background = false
}: React.PropsWithChildren<ProgressBarProps>) => {
  const primaryValue = useMemo(() => {
    const v = typeof value === 'object' ? value[0] : value;
    return clamp(v, 0, 100);
  }, [value]);

  const secondaryValue = useMemo(() => {
    const v = typeof value === 'object' ? value[1] : 100;
    return clamp(v, 0, 100);
  }, [value]);

  return (
    <div
      className={getClassName('progressbar-container', {
        focused: active,
        safe: safeArea
      })}
      ref={customRef}
    >
      {background && <ProgressBarValue type="background" value={100} />}
      <ProgressBarValue type="secondary" value={secondaryValue} />
      <ProgressBarValue type="primary" value={primaryValue} />
      {children}
    </div>
  );
};

export default React.memo(Progressbar);
